<script setup lang="ts">
/**
 * RinganKompresAlat — alat kompres gratis, versi tanpa Nuxt UI.
 *
 * SELURUH LOGIKA DISALIN APA ADANYA dari CaKompresAlat.vue: kontrak API,
 * ambang batas, urutan validasi, dan setiap teks galat Bahasa Indonesia.
 * Yang berubah hanya lapisan tampilannya — <UIcon>/<CaButton>/<CaInput>
 * ditukar dengan padanan ringan yang menghasilkan kelas CSS yang sama.
 * Kalau logikanya perlu diubah, DUA berkas ini harus diubah bersamaan.
 *
 * Kontrak backend TIDAK BERUBAH:
 *   POST {apiBase}/api/gratis/kompres
 *   multipart: "file" + "target_kb", tanpa token.
 *   Balasan: nama_file, mime, target_kb, ukuran_awal_byte, ukuran_akhir_byte,
 *            tercapai, pesan, perubahan[], data_base64. Galat: { error }.
 *
 * Catatan kejujuran: alat ini memproses berkas DI SERVER (di memori, tidak
 * ditulis ke penyimpanan). Jangan menulis "diproses di perangkat Anda" di mana
 * pun pada halaman yang memakai komponen ini — itu tidak benar.
 */
const props = withDefaults(
  defineProps<{
    jenis: 'foto' | 'pdf'
    targetAwal?: number
  }>(),
  { targetAwal: 200 }
)

const apiBase = useApiBase()

const MAKS_BYTE = 15 * 1024 * 1024
// Cermin gratisTargetMaks di internal/httpapi/gratis.go. Target yang lebih
// besar dari ini ditolak server 400, jadi jangan dinaikkan sendirian di sini.
// Batasnya ada supaya berkas besar benar-benar dikompres, bukan dipulangkan
// utuh (JPEG yang sudah masuk rentang sengaja tidak di-encode ulang).
const TARGET_MAKS = 2000

const PRESET: Record<'foto' | 'pdf', number[]> = {
  foto: [100, 200, 300, 500, 800],
  pdf: [200, 300, 500, 800]
}

const terima = computed(() =>
  props.jenis === 'foto' ? 'image/jpeg,image/png,image/webp' : 'application/pdf'
)
const presetTarget = computed(() => PRESET[props.jenis])
const catatanBerkas = computed(() =>
  props.jenis === 'foto'
    ? 'JPG, PNG, atau WebP · satu berkas · maksimal 15 MB'
    : 'PDF · satu berkas · maksimal 15 MB'
)

const berkas = ref<File | null>(null)
const target = ref<number>(props.targetAwal)
const targetKustom = ref<number | null>(null)
const pakaiKustom = ref(false)
const memuat = ref(false)
const galat = ref('')

interface Hasil {
  namaFile: string
  ukuranAwal: number
  ukuranAkhir: number
  tercapai: boolean
  pesan: string
  perubahan: string[]
  url: string
}

const hasil = ref<Hasil | null>(null)

// ---- Pratinjau gambar, versi SUPER RINGAN (khusus halaman kompres SEO) ----
// Sengaja TIDAK memakai CaLightbox (yang membawa logika zoom/putar/pan): di
// halaman 'ringan' anggaran ketat dan tidak ada galeri. Ini cuma <dialog>
// native + <img object-contain> + ketuk-untuk-tutup. Tanpa zoom/putar JS.
const dialogRingan = ref<HTMLDialogElement | null>(null)
const lbSrc = ref('')
const lbNama = ref('')
const pratinjauInput = ref('')

function lepasPratinjauInput() {
  if (pratinjauInput.value) {
    URL.revokeObjectURL(pratinjauInput.value)
    pratinjauInput.value = ''
  }
}

watch(berkas, (f) => {
  lepasPratinjauInput()
  if (f && props.jenis === 'foto' && f.type.startsWith('image/')) {
    pratinjauInput.value = URL.createObjectURL(f)
  }
})

const pratinjauHasil = computed(() =>
  hasil.value && props.jenis === 'foto' ? hasil.value.url : ''
)

function bukaPratinjau(src: string, nama: string) {
  if (!src) return
  lbSrc.value = src
  lbNama.value = nama
  dialogRingan.value?.showModal()
}
function tutupPratinjau() {
  if (dialogRingan.value?.open) dialogRingan.value.close()
}

const targetEfektif = computed(() =>
  pakaiKustom.value ? Number(targetKustom.value || 0) : target.value
)

const persenHemat = computed(() => {
  if (!hasil.value || hasil.value.ukuranAwal <= 0) return 0
  const h = 1 - hasil.value.ukuranAkhir / hasil.value.ukuranAwal
  return Math.max(0, Math.round(h * 100))
})

/**
 * hematBerarti — kembar dari berkas.AmbangHematPersen (2%) di server.
 *
 * Di bawah ambang ini berkasnya praktis tidak berubah, jadi menampilkan
 * "Selesai, ukuran turun 0%" dengan centang hijau adalah perayaan palsu. PDF
 * berbasis teks dan pindaian yang sudah padat memang begitu adanya, dan
 * pengguna berhak tahu penyebabnya alih-alih mengira alatnya rusak.
 */
const hematBerarti = computed(() => {
  if (!hasil.value || hasil.value.ukuranAwal <= 0) return false
  return (1 - hasil.value.ukuranAkhir / hasil.value.ukuranAwal) * 100 >= 2
})

/**
 * 1 KB = 1000 byte (desimal), sama dengan berkas.BytePerKB di server — tafsir
 * aman yang dipakai seluruh produk. Lihat README bagian "Penafsiran satuan KB".
 */
const BYTE_PER_KB = 1000

function formatUkuran(byte: number): string {
  if (byte >= 1000 * BYTE_PER_KB) return (byte / (1000 * BYTE_PER_KB)).toFixed(2) + ' MB'
  return Math.round(byte / BYTE_PER_KB) + ' KB'
}

function bersihkanHasil() {
  if (hasil.value) URL.revokeObjectURL(hasil.value.url)
  hasil.value = null
}

function pilihBerkas(daftar: File[]) {
  const f = daftar[0]
  galat.value = ''
  bersihkanHasil()
  if (!f) return
  if (f.size > MAKS_BYTE) {
    galat.value = `Ukuran berkas ${formatUkuran(f.size)} melebihi batas 15 MB.`
    return
  }
  if (/\.(heic|heif)$/i.test(f.name)) {
    galat.value =
      'Format HEIC/HEIF belum didukung. Buka foto di aplikasi Foto lalu simpan atau ekspor sebagai JPEG, kemudian unggah lagi.'
    return
  }
  berkas.value = f
}

function base64KeBlob(b64: string, tipe: string): Blob {
  const bin = atob(b64)
  const arr = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i += 1) arr[i] = bin.charCodeAt(i)
  return new Blob([arr], { type: tipe })
}

async function proses() {
  galat.value = ''
  if (!berkas.value) {
    galat.value = 'Pilih berkas dulu.'
    return
  }
  const t = targetEfektif.value
  if (!Number.isFinite(t) || t < 5 || t > TARGET_MAKS) {
    galat.value = `Target ukuran harus antara 5 KB dan ${TARGET_MAKS.toLocaleString('id-ID')} KB.`
    return
  }

  bersihkanHasil()
  memuat.value = true
  const ukuranAwal = berkas.value.size

  try {
    const fd = new FormData()
    fd.append('file', berkas.value)
    fd.append('target_kb', String(t))

    const res = await fetch(`${apiBase}/api/gratis/kompres`, { method: 'POST', body: fd })

    if (!res.ok) {
      let pesan = `Gagal memproses berkas (kode ${res.status}).`
      if (res.status === 429) {
        pesan = 'Terlalu banyak permintaan dari jaringan ini. Tunggu sebentar lalu coba lagi.'
      } else {
        try {
          const j = (await res.json()) as { error?: string }
          if (j?.error) pesan = String(j.error)
        } catch {
          // Balasan bukan JSON — pakai pesan bawaan.
        }
      }
      galat.value = pesan
      return
    }

    const j = (await res.json()) as {
      nama_file?: string
      mime?: string
      ukuran_awal_byte?: number
      ukuran_akhir_byte?: number
      tercapai?: boolean
      pesan?: string
      perubahan?: string[]
      data_base64?: string
    }

    const blob = base64KeBlob(j.data_base64 || '', j.mime || 'application/octet-stream')
    hasil.value = {
      namaFile: j.nama_file || 'hasil',
      ukuranAwal: Number(j.ukuran_awal_byte ?? ukuranAwal),
      ukuranAkhir: Number(j.ukuran_akhir_byte ?? blob.size),
      tercapai: j.tercapai !== false,
      pesan: String(j.pesan || ''),
      perubahan: Array.isArray(j.perubahan) ? j.perubahan : [],
      url: URL.createObjectURL(blob)
    }
  } catch {
    galat.value = 'Gagal menghubungi server. Periksa koneksi lalu coba lagi.'
  } finally {
    memuat.value = false
  }
}

function ulangi() {
  bersihkanHasil()
  berkas.value = null
  galat.value = ''
}

function pilihPreset(p: number) {
  pakaiKustom.value = false
  target.value = p
}

onBeforeUnmount(() => {
  bersihkanHasil()
  lepasPratinjauInput()
})

/** Kelas <input> kustom — disalin dari CaInput (min 16px teks & 48px tinggi,
    supaya Android/iOS tidak melakukan zoom paksa saat kolom disentuh),
    digabung padding ukuran `md` bawaan UInput. */
const kelasInput = [
  'w-full min-h-12 px-2.5 py-1.5 rounded-control bg-page border border-line text-ink',
  'text-base placeholder:text-muted',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-ring-ca)]'
].join(' ')
</script>

<template>
  <CaCard tone="plain" class="shadow-float">
    <!-- 1. BERKAS -->
    <RinganDropzone
      v-if="!berkas"
      :accept="terima"
      :multiple="false"
      :judul="jenis === 'foto' ? 'Pilih foto yang mau dikecilkan' : 'Pilih berkas PDF'"
      :catatan="catatanBerkas"
      @pilih="pilihBerkas"
    />

    <div v-else class="flex items-center gap-3.5 rounded-card border border-line bg-surface-2 p-4">
      <button
        v-if="pratinjauInput"
        type="button"
        class="size-11 shrink-0 overflow-hidden rounded-control border border-line"
        :aria-label="`Lihat ${berkas.name} lebih besar`"
        @click="bukaPratinjau(pratinjauInput, berkas.name)"
      >
        <img :src="pratinjauInput" alt="" class="size-full object-cover">
      </button>
      <span
        v-else
        class="ca-ikon-emas grid place-items-center size-11 shrink-0 rounded-control"
        aria-hidden="true"
      >
        <RinganIkon :nama="jenis === 'foto' ? 'image' : 'file-text'" class="size-5" />
      </span>
      <div class="min-w-0 flex-1">
        <p class="font-bold text-ink leading-snug truncate">{{ berkas.name }}</p>
        <p class="ca-caption text-muted mt-0.5">{{ formatUkuran(berkas.size) }}</p>
      </div>
      <RinganTombol variant="ghost" size="md" class="shrink-0" @click="ulangi">Ganti</RinganTombol>
    </div>

    <!-- 2. TARGET UKURAN -->
    <div class="mt-6">
      <p class="text-sm font-semibold text-ink leading-snug">Target ukuran</p>
      <p class="ca-caption text-muted mt-1">
        Pilih angka yang diminta portal tujuan Anda. Hasilnya akan sedikit di bawah angka itu.
      </p>

      <div class="mt-3 flex flex-wrap gap-2">
        <RinganTombol
          v-for="p in presetTarget"
          :key="p"
          size="md"
          :variant="!pakaiKustom && target === p ? 'primary' : 'secondary'"
          :aria-pressed="!pakaiKustom && target === p"
          @click="pilihPreset(p)"
        >{{ p }} KB</RinganTombol>

        <RinganTombol
          size="md"
          :variant="pakaiKustom ? 'primary' : 'secondary'"
          :aria-pressed="pakaiKustom"
          @click="pakaiKustom = true"
        >Tentukan sendiri</RinganTombol>
      </div>

      <!-- Padanan <CaField> + <CaInput>, ditulis langsung di sini: satu kolom
           angka tidak cukup rumit untuk pantas jadi komponen tersendiri. -->
      <div v-if="pakaiKustom" class="mt-4 max-w-[14rem]">
        <div class="flex flex-col gap-2">
          <label for="kompres-target-kustom" class="text-sm font-semibold text-ink leading-snug">
            Target sendiri (KB)
          </label>
          <input
            id="kompres-target-kustom"
            :value="targetKustom === null ? '' : String(targetKustom)"
            type="number"
            inputmode="numeric"
            placeholder="mis. 250"
            aria-describedby="kompres-target-kustom-hint"
            :class="kelasInput"
            @input="
              (e) => {
                const v = (e.target as HTMLInputElement).value
                targetKustom = v === '' ? null : Number(v)
              }
            "
          >
          <p id="kompres-target-kustom-hint" class="ca-caption text-muted">Antara 5 dan 2.000 KB</p>
        </div>
      </div>
    </div>

    <!-- 3. GALAT -->
    <RinganPesan v-if="galat" tone="bad" title="Belum bisa diproses" class="mt-6">
      {{ galat }}
    </RinganPesan>

    <!-- 4. AKSI -->
    <div class="mt-6">
      <RinganTombol
        variant="emas"
        size="lg"
        block
        :loading="memuat"
        :disabled="memuat || !berkas"
        @click="proses"
      >{{ memuat ? 'Memproses…' : 'Kecilkan sekarang' }}</RinganTombol>
      <p class="ca-caption text-muted mt-3 text-center">
        Gratis, tanpa akun. Berkas diproses di memori server dan tidak disimpan.
      </p>
    </div>

    <!-- 5. HASIL -->
    <div v-if="hasil" class="mt-7 border-t border-line pt-6">
      <RinganPesan v-if="hasil.tercapai && hematBerarti" tone="ok" title="Selesai">
        Ukuran turun {{ persenHemat }}% dari berkas asli.
      </RinganPesan>
      <RinganPesan
        v-else-if="hasil.tercapai"
        tone="info"
        title="Ukuran hampir tidak berubah"
      >
        Berkas ini sudah padat, jadi tidak ada yang bisa dipangkas tanpa merusak isinya. Ukurannya
        sudah di bawah {{ targetEfektif }} KB, jadi berkas asli Anda sebenarnya sudah bisa dipakai.
      </RinganPesan>
      <RinganPesan v-else tone="warn" title="Target belum tercapai">
        Target {{ targetEfektif }} KB tidak tercapai tanpa membuat berkas sulit dibaca. Hasil terbaik
        yang bisa dicapai adalah {{ formatUkuran(hasil.ukuranAkhir) }}.
      </RinganPesan>

      <p v-if="hasil.pesan" class="ca-caption text-muted mt-3 leading-relaxed">{{ hasil.pesan }}</p>
      <ul v-if="hasil.perubahan.length" class="mt-3 flex flex-col gap-1.5">
        <li
          v-for="(p, i) in hasil.perubahan"
          :key="i"
          class="ca-caption text-muted flex gap-2 leading-relaxed"
        >
          <!-- Centang hijau hanya kalau memang ada yang dikerjakan; kalau tidak,
               ikon netral supaya daftar ini tidak terbaca sebagai keberhasilan. -->
          <RinganIkon
            :nama="hematBerarti ? 'check' : 'info'"
            class="size-4 shrink-0 mt-px"
            :class="hematBerarti ? 'text-ok' : 'text-muted'"
          />
          <span>{{ p }}</span>
        </li>
      </ul>

      <!-- Pratinjau hasil (khusus foto), ketuk untuk lihat penuh. -->
      <button
        v-if="pratinjauHasil"
        type="button"
        class="mt-5 block w-full overflow-hidden rounded-card border border-line bg-surface-2"
        aria-label="Lihat hasil lebih besar"
        @click="bukaPratinjau(pratinjauHasil, hasil.namaFile)"
      >
        <img :src="pratinjauHasil" alt="Pratinjau hasil kompres" class="mx-auto max-h-56 w-auto object-contain">
      </button>

      <!-- Padanan <CaStat> untuk pasangan sebelum → sesudah. -->
      <div class="mt-5 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
        <div class="rounded-control border border-line bg-surface-2 p-3.5">
          <div class="flex items-center gap-2 text-muted">
            <RinganIkon nama="file" class="size-4 shrink-0" />
            <p class="ca-caption font-medium truncate">Sebelum</p>
          </div>
          <p class="ca-num mt-1.5 text-xl font-extrabold text-ink leading-none">
            {{ formatUkuran(hasil.ukuranAwal) }}
          </p>
        </div>

        <RinganIkon nama="arrow-right" class="size-5 text-muted shrink-0" />

        <div class="rounded-control border border-line bg-surface-2 p-3.5">
          <div class="flex items-center gap-2 text-muted">
            <RinganIkon nama="file-check" class="size-4 shrink-0" />
            <p class="ca-caption font-medium truncate">Sesudah</p>
          </div>
          <p class="ca-num mt-1.5 text-xl font-extrabold text-ink leading-none">
            {{ formatUkuran(hasil.ukuranAkhir) }}
          </p>
        </div>
      </div>

      <div class="mt-5 flex flex-col sm:flex-row gap-3">
        <RinganTombol
          variant="emas"
          size="lg"
          icon="download"
          :href="hasil.url"
          :download="hasil.namaFile"
        >Unduh hasil</RinganTombol>
        <RinganTombol variant="secondary" size="lg" @click="ulangi">Proses berkas lain</RinganTombol>
      </div>
    </div>

    <!-- Pratinjau super-ringan: <dialog> native (ESC + focus-trap gratis),
         ketuk di mana saja untuk menutup. Tanpa zoom/putar JS. -->
    <Teleport to="body">
      <dialog
        ref="dialogRingan"
        class="ringan-lb"
        :aria-label="lbNama || 'Pratinjau gambar'"
        @click="tutupPratinjau"
      >
        <img :src="lbSrc" :alt="lbNama || 'Pratinjau gambar'" class="ringan-lb-img">
        <span class="ringan-lb-tutup" aria-hidden="true">Ketuk untuk menutup</span>
      </dialog>
    </Teleport>
  </CaCard>
</template>

<style scoped>
.ringan-lb {
  margin: 0;
  padding: 0;
  border: 0;
  width: 100vw;
  height: 100dvh;
  max-width: 100vw;
  max-height: 100dvh;
  background: transparent;
  display: grid;
  place-items: center;
}
.ringan-lb::backdrop {
  background: rgba(5, 8, 20, 0.92);
}
.ringan-lb-img {
  max-width: 94vw;
  max-height: 82dvh;
  object-fit: contain;
}
.ringan-lb-tutup {
  position: fixed;
  bottom: max(1rem, env(safe-area-inset-bottom));
  left: 50%;
  transform: translateX(-50%);
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.8rem;
}
</style>
